import React, { useEffect, useState, createElement } from 'react';
import { CheckCircle2, XCircle, Clock, Star, FileText, Zap, ShieldCheck, Users, BookOpen, Gift, ChevronDown, AlertCircle, ArrowRight, MessageSquare, Calendar, Layers, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}
// --- Sub-components ---
const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 27,
    seconds: 35
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return {
          ...prev,
          seconds: prev.seconds - 1
        };
        if (prev.minutes > 0) return {
          ...prev,
          minutes: prev.minutes - 1,
          seconds: 59
        };
        if (prev.hours > 0) return {
          ...prev,
          hours: prev.hours - 1,
          minutes: 59,
          seconds: 59
        };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  return <div className="flex gap-2 font-mono text-xl md:text-2xl font-bold text-white" data-id="element-1">
      <div className="bg-[#C62828] px-2 py-1 rounded border border-white/20" data-id="element-2">
        {String(timeLeft.hours).padStart(2, '0')}h
      </div>
      <span className="text-white" data-id="element-3">:</span>
      <div className="bg-[#C62828] px-2 py-1 rounded border border-white/20" data-id="element-4">
        {String(timeLeft.minutes).padStart(2, '0')}m
      </div>
      <span className="text-white" data-id="element-5">:</span>
      <div className="bg-[#C62828] px-2 py-1 rounded border border-white/20" data-id="element-6">
        {String(timeLeft.seconds).padStart(2, '0')}s
      </div>
    </div>;
};
const FloatingNotification = () => {
  const [visible, setVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const notifications = [{
    n: 'Laura G.',
    l: 'Buenos Aires',
    t: 'hace 6 minutos',
    p: 'el Pack Completo 🚨'
  }, {
    n: 'Marcos F.',
    l: 'Córdoba',
    t: 'hace 2 minutos',
    p: 'el Pack Completo 🚨'
  }, {
    n: 'Valeria R.',
    l: 'Rosario',
    t: 'hace 15 minutos',
    p: 'el Pack Completo 🚨'
  }, {
    n: 'Daniela M.',
    l: 'Mendoza',
    t: 'hace 8 minutos',
    p: 'el Pack Completo 🚨'
  }];
  useEffect(() => {
    const initialTimeout = setTimeout(() => setVisible(true), 15000);
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % notifications.length);
        setVisible(true);
      }, 1000);
    }, 15000);
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, []);
  const current = notifications[currentIndex];
  return <AnimatePresence data-id="element-7">
      {visible && <motion.div initial={{
      opacity: 0,
      y: 50,
      x: -20
    }} animate={{
      opacity: 1,
      y: 0,
      x: 0
    }} exit={{
      opacity: 0,
      y: 20
    }} className="fixed bottom-6 left-6 z-50 bg-white shadow-2xl rounded-lg p-4 border border-slate-100 flex items-center gap-4 max-w-sm" data-id="element-8">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-[#10A37F]" data-id="element-9">
            <Check size={20} data-id="element-10" />
          </div>
          <div data-id="element-11">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-12">
              Compra verificada
            </p>
            <p className="text-sm text-slate-800" data-id="element-13">
              <span className="font-bold" data-id="element-14">{current.n}</span> de {current.l}
              <br data-id="element-15" />
              compró {current.p}
            </p>
            <p className="text-xs text-slate-400 mt-1" data-id="element-16">{current.t}</p>
          </div>
          <button onClick={() => setVisible(false)} className="text-slate-300 hover:text-slate-500" data-id="element-17">
            <XCircle size={16} data-id="element-18" />
          </button>
        </motion.div>}
    </AnimatePresence>;
};
const SectionTitle = ({
  children,
  subtitle,
  light = false
}: {
  children: React.ReactNode;
  subtitle?: string;
  light?: boolean;
}) => <div className="text-center mb-6 md:mb-10" data-id="element-19">
    <h2 className={`text-3xl md:text-5xl font-bold mb-3 md:mb-4 ${light ? 'text-white' : 'text-slate-900'}`} data-id="element-20">
      {children}
    </h2>
    {subtitle && <p className={`text-base md:text-lg ${light ? 'text-slate-200' : 'text-slate-600'} max-w-2xl mx-auto font-sans px-4`} data-id="element-21">
        {subtitle}
      </p>}
  </div>;
const CTAButton = ({
  children,
  secondary = false,
  className = '',
  href = '',
  onClick
}: {
  children: React.ReactNode;
  secondary?: boolean;
  className?: string;
  href?: string;
  onClick?: () => void;
}) => {
  const handleClick = (e: React.MouseEvent) => {
    if (window.fbq) {
      if (href.includes('cart')) {
        const isTier2 = href.includes('44367122530397');
        window.fbq('track', 'InitiateCheckout', {
          content_name: isTier2 ? 'Colección + Organizador' : 'Colección Completa',
          value: isTier2 ? 24990 : 19990,
          currency: 'ARS'
        });
      } else if (href === '#pricing') {
        window.fbq('track', 'ViewContent', {
          content_name: 'Pricing Section'
        });
      }
    }
    if (onClick) onClick();
  };
  const content = <motion.button animate={{
    scale: [1, 1.02, 1]
  }} transition={{
    duration: 2,
    repeat: Infinity,
    ease: 'easeInOut'
  }} className={`
        w-full md:w-auto px-10 py-5 rounded-full font-bold text-lg transition-all transform hover:scale-105 active:scale-95 shadow-xl
        ${secondary ? 'bg-white text-[#1A237E] border-2 border-slate-200 hover:bg-slate-50' : 'bg-[#E65100] text-white hover:bg-[#d84315]'}
        ${className}
      `} onClick={handleClick} data-id="element-22">
      {children}
    </motion.button>;
  if (href) {
    const isInternal = href.startsWith('#');
    return <a href={href} target={isInternal ? undefined : '_blank'} rel={isInternal ? undefined : 'noopener noreferrer'} className="w-full md:w-auto block" onClick={e => {
      handleClick(e);
      if (isInternal) {
        e.preventDefault();
        const element = document.querySelector(href);
        if (element) element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }} data-id="element-23">
        {content}
      </a>;
  }
  return content;
};
// --- Main Page ---
export function LandingPage() {
  useEffect(() => {
    if (!window.fbq) {
      const f = window as any;
      const b = document;
      const e = 'script';
      const v = 'https://connect.facebook.net/en_US/fbevents.js';
      let n: any, t: any, s: any;
      f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      n = f.fbq;
      if (!f._fbq) f._fbq = n;
      f.fbq.push = f.fbq;
      f.fbq.loaded = !0;
      f.fbq.version = '2.0';
      f.fbq.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      if (s && s.parentNode) {
        s.parentNode.insertBefore(t, s);
      } else {
        b.head.appendChild(t);
      }
    }
    if (window.fbq) {
      window.fbq('init', '1313291250496067');
      window.fbq('track', 'PageView');
    }
  }, []);
  return <div className="min-h-screen font-sans" data-id="element-24">
      <FloatingNotification data-id="element-25" />

      {/* Meta Pixel Noscript */}
      <noscript data-id="element-26">
        <img height="1" width="1" style={{
        display: 'none'
      }} src="https://www.facebook.com/tr?id=1313291250496067&ev=PageView&noscript=1" data-id="element-27" />
      </noscript>

      {/* Hero Section */}
      <header className="relative bg-[#1A237E] min-h-[100svh] flex flex-col text-white overflow-hidden" data-id="element-28">
        <div className="absolute top-0 left-0 w-full h-1 flex" data-id="element-29">
          <div className="flex-1 bg-white/20" data-id="element-30"></div>
          <div className="flex-1 bg-white/40" data-id="element-31"></div>
          <div className="flex-1 bg-white/20" data-id="element-32"></div>
        </div>

        <div className="container mx-auto px-4 flex-grow flex flex-col justify-center py-8 md:py-16 relative z-10" data-id="element-33">
          <div className="max-w-4xl mx-auto text-center flex flex-col h-full" data-id="element-34">
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full text-white text-[10px] md:text-sm font-bold mb-4 md:mb-8 backdrop-blur-sm self-center" data-id="element-35">
              🚨 CÓDIGO EMERGENCIA ESCOLAR™
            </motion.div>

            <h1 className="md:text-7xl font-bold mb-4 md:mb-8 text-white text-[24px]" data-id="element-36">
              Un protocolo claro para saber qué decir y cómo actuar ante una
              amenaza escolar.
            </h1>

            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2,
            duration: 0.8
          }} className="relative max-w-[320px] md:max-w-4xl mx-auto mb-6 md:mb-12 group" data-id="element-37">
              <div className="absolute inset-0 bg-[#E65100] blur-3xl opacity-20 group-hover:opacity-40 transition-opacity duration-700" data-id="element-38"></div>
              <img src="https://cdn.magicpatterns.com/uploads/rzfibkpJEqjQkydDx9baQw/codigo-emergencia-escolar-optimized.webp" alt="Código Emergencia Escolar Mockup" className="relative z-10 w-full h-auto rounded-xl shadow-2xl" data-id="element-39" />
            </motion.div>

            <p className="text-lg md:text-2xl text-white/80 mb-6 md:mb-10 max-w-2xl mx-auto leading-snug md:leading-relaxed px-4" data-id="element-40">
              Hay una manera clara de actuar ante una amenaza en la escuela.{' '}
              <br data-id="element-41" />
              <span className="font-bold text-white" data-id="element-42">Y podés empezar hoy.</span>
            </p>

            <div className="flex flex-col items-center gap-4 md:gap-6 mb-6 md:mb-12" data-id="element-43">
              <CTAButton href="#pricing" className="uppercase tracking-wide px-8 py-4 md:px-12 md:py-5 text-sm md:text-lg" data-id="element-44">
                → QUIERO TENER LAS HERRAMIENTAS DESDE HOY
              </CTAButton>

              <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-[#1A237E] font-medium text-[10px] md:text-sm" data-id="element-45">
                <div className="flex items-center gap-1 bg-white/90 px-3 py-1 rounded-full" data-id="element-46">
                  <FileText size={14} data-id="element-47" /> 100% PDF
                </div>
                <div className="flex items-center gap-1 bg-white/90 px-3 py-1 rounded-full" data-id="element-48">
                  <Zap size={14} data-id="element-49" /> Acceso en 5 min
                </div>
                <div className="flex items-center gap-1 bg-white/90 px-3 py-1 rounded-full" data-id="element-50">
                  <ShieldCheck size={14} data-id="element-51" /> Garantía 7 días
                </div>
              </div>
            </div>

            <div className="bg-[#C62828] border border-white/20 rounded-xl p-4 md:p-6 shadow-xl inline-block self-center" data-id="element-52">
              <p className="text-white font-bold uppercase tracking-widest text-[10px] md:text-sm mb-2 md:mb-3" data-id="element-53">
                ⏰ CIERRA EN:
              </p>
              <CountdownTimer data-id="element-54" />
            </div>
          </div>
        </div>
      </header>

      {/* Stats Section */}
      <section className="py-8 md:py-12 bg-[#1A237E] border-b border-white/10" data-id="element-55">
        <div className="container mx-auto px-4" data-id="element-56">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto text-center" data-id="element-57">
            {[{
            label: 'docentes y familias',
            value: '+500'
          }, {
            label: 'módulos y bonos',
            value: '7'
          }, {
            label: 'páginas listas',
            value: '270+'
          }, {
            label: 'calificación',
            value: '4.9/5',
            stars: true
          }].map((stat, i) => <div key={i} data-id="element-58">
                <p className="text-3xl md:text-4xl font-bold text-[#E65100] mb-1" data-id="element-59">
                  {stat.value}
                </p>
                {stat.stars && <div className="flex justify-center text-yellow-500 mb-1" data-id="element-60">
                    {[...Array(5)].map((_, j) => <Star key={j} size={14} fill="currentColor" data-id="element-61" />)}
                  </div>}
                <p className="text-[10px] md:text-xs uppercase tracking-widest text-white font-bold" data-id="element-62">
                  {stat.label}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Testimonials 1 */}
      <section className="py-10 md:py-20 bg-white" data-id="element-63">
        <div className="container mx-auto px-4" data-id="element-64">
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-65">
            {[{
            n: 'Laura G.',
            l: 'Buenos Aires · Docente de secundaria',
            q: 'Llegué a la reunión de padres sin saber qué decir. Con este material tuve un taller completo listo para usar en 24 horas.',
            i: 'LG'
          }, {
            n: 'Marcos F.',
            l: 'Córdoba · Director de escuela privada',
            q: 'El Módulo 2 me salvó cuando recibimos una amenaza anónima en el grupo de WhatsApp. Supe exactamente qué hacer en los primeros 60 minutos.',
            i: 'MF'
          }, {
            n: 'Valeria R.',
            l: 'Rosario · Orientadora escolar',
            q: 'Por fin un material que habla en argentino, con leyes argentinas y situaciones reales de nuestras escuelas.',
            i: 'VR'
          }].map((test, i) => <div key={i} className="bg-[#F5F5F5] p-8 rounded-2xl shadow-sm border border-slate-100" data-id="element-66">
                <div className="flex gap-1 mb-4 text-yellow-500" data-id="element-67">
                  {[...Array(5)].map((_, j) => <Star key={j} size={16} fill="currentColor" data-id="element-68" />)}
                </div>
                <p className="text-[#37474F] italic mb-6 leading-relaxed" data-id="element-69">
                  "{test.q}"
                </p>
                <div className="flex items-center gap-4" data-id="element-70">
                  <div className="w-10 h-10 bg-[#1A237E] rounded-full flex items-center justify-center font-bold text-white" data-id="element-71">
                    {test.i}
                  </div>
                  <div data-id="element-72">
                    <p className="font-bold text-[#1A237E]" data-id="element-73">{test.n}</p>
                    <p className="text-xs text-[#37474F]" data-id="element-74">{test.l}</p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-10 md:py-20 bg-[#ECEFF1]" data-id="element-75">
        <div className="container mx-auto px-4" data-id="element-76">
          <SectionTitle subtitle="Llegó un mensaje amenazante al grupo de WhatsApp del curso. O alguien difundió una imagen de una alumna. Y en ese momento no hay tiempo para improvisar." data-id="element-77">
            <span className="text-[#1A237E]" data-id="element-78">
              ¿Cuántas situaciones más vas a enfrentar sin saber exactamente qué
              hacer?
            </span>
          </SectionTitle>

          <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16" data-id="element-79">
            <p className="text-lg text-[#37474F] leading-relaxed mb-6" data-id="element-80">
              O un padre furioso llega a la dirección exigiendo respuestas. O
              descubrís que un alumno estuvo en contacto con un adulto
              desconocido por Instagram. No hay tiempo para buscar en Google. No
              hay tiempo para equivocarse.
            </p>
            <p className="text-lg text-[#37474F] leading-relaxed mb-6" data-id="element-81">
              Nadie te preparó para ese silencio en la sala de profesores cuando
              algo grave pasa y todos te miran esperando que sepas qué hacer.
            </p>
            <p className="text-xl font-bold text-[#E65100]" data-id="element-82">
              No es falta de vocación. Es falta de herramientas concretas.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto" data-id="element-83">
            {['Sin saber qué constituye un delito digital en Argentina', 'Sin un protocolo claro para las primeras horas', 'Sin materiales listos para trabajar con alumnos', 'Sin saber cómo hablarle a las familias sin generar pánico', 'Con miedo a decir algo incorrecto legalmente', 'Sin recursos para que los alumnos reflexionen', 'Sabiendo que algo puede pasar y sin estar preparado/a'].map((item, i) => <div key={i} className="bg-white p-4 rounded-lg flex gap-3 items-start" data-id="element-84">
                <XCircle className="text-[#C62828] shrink-0 mt-1" size={20} data-id="element-85" />
                <p className="text-[#37474F] font-medium" data-id="element-86">{item}</p>
              </div>)}
          </div>

          <div className="mt-12 max-w-3xl mx-auto bg-[#FFF8E1] border-l-8 border-[#E65100] p-8 rounded-r-2xl shadow-sm text-center" data-id="element-87">
            <p className="text-2xl font-bold text-[#1A237E] italic" data-id="element-88">
              ¿Y si esta fuera la última vez que enfrentás una situación así
              completamente sin herramientas?
            </p>
          </div>
        </div>
      </section>

      {/* Product Hero */}
      <section className="py-10 md:py-20 bg-[#1A237E] text-white" data-id="element-89">
        <div className="container mx-auto px-4 text-center" data-id="element-90">
          <div className="inline-block bg-[#E65100] text-white px-6 py-2 rounded-full font-bold text-sm uppercase tracking-widest mb-8" data-id="element-91">
            SOLUCIÓN INTEGRAL
          </div>
          <h2 className="text-3xl md:text-6xl font-bold mb-4 text-white" data-id="element-92">
            🚨 CÓDIGO EMERGENCIA ESCOLAR™ PACK COMPLETO
          </h2>
          <p className="text-lg md:text-2xl text-white/70 mb-12 max-w-3xl mx-auto" data-id="element-93">
            4 Módulos + 3 Bonos · 270+ páginas · Todo lo que necesitás para
            prevenir, actuar y acompañar.
          </p>

          <div className="max-w-4xl mx-auto mb-12 relative" data-id="element-94">
            <img src="https://cdn.magicpatterns.com/uploads/rzfibkpJEqjQkydDx9baQw/codigo-emergencia-escolar-optimized.webp" alt="Pack Completo Mockup" className="w-full h-auto rounded-3xl shadow-2xl border-2 border-white/10" data-id="element-95" />
          </div>

          <div className="max-w-3xl mx-auto bg-[#283593] border-l-8 border-[#E65100] p-6 rounded-r-xl text-left" data-id="element-96">
            <p className="text-lg md:text-xl font-bold text-white mb-2" data-id="element-97">
              Desarrollado por especialistas en derecho y pedagogía.
            </p>
            <p className="text-white/80" data-id="element-98">
              100% adaptado a la realidad legal y educativa argentina.
            </p>
          </div>
        </div>
      </section>

      {/* The 4 Modules */}
      <section className="py-10 md:py-20 bg-white" data-id="element-99">
        <div className="container mx-auto px-4" data-id="element-100">
          <SectionTitle subtitle="Cada módulo incluye protocolos, dinámicas y materiales listos para usar." data-id="element-101">
            <span className="text-[#1A237E]" data-id="element-102">📚 4 MÓDULOS COMPLETOS</span>
          </SectionTitle>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto" data-id="element-103">
            {[{
            t: 'MÓDULO 1 — MARCO LEGAL',
            bg: 'bg-[#1A237E]',
            d: 'Lo que la ley dice en lenguaje que cualquier docente puede entender y usar.',
            f: ['Artículos del Código Penal explicados', 'Qué es delito y qué no', 'Responsabilidad del menor en Argentina', 'Ley de Delitos Informáticos 26.388', 'Protocolo de preservación de evidencia']
          }, {
            t: 'MÓDULO 2 — PROTOCOLO DE CRISIS',
            bg: 'bg-[#0D47A1]',
            d: 'Qué hacer en los primeros 60 minutos ante una amenaza. Paso a paso institucional.',
            f: ['Árbol de decisión ante amenaza', 'Cadena de comunicación oficial', 'Cómo preservar evidencia digital', 'Cuándo y cómo llamar a la fiscalía', 'Formularios y registros oficiales']
          }, {
            t: 'MÓDULO 3 — EL TALLER PARTICIPATIVO',
            bg: 'bg-[#283593]',
            d: '9 dinámicas activas listas para usar con alumnos de secundaria hoy mismo.',
            f: ['Actividades disparadoras de reflexión', 'Análisis de casos ficticios realistas', 'Rol del testigo digital', 'Acuerdo de convivencia digital', 'Recursos imprimibles para el aula']
          }, {
            t: 'MÓDULO 4 — ALIANZA ESCUELA-FAMILIA',
            bg: 'bg-[#1A237E]',
            d: 'La guía completa para la reunión de padres. Todo listo para facilitar en 90 minutos.',
            f: ['Agenda de reunión lista para usar', 'Información legal adaptada a familias', 'Dinámicas participativas para padres', 'Acuerdo familiar de uso digital', 'Kit de materiales para llevar a casa']
          }].map((tomo, i) => <div key={i} className={`p-8 rounded-2xl ${tomo.bg} shadow-xl hover:scale-[1.02] transition-all`} data-id="element-104">
                <h3 className="text-xl font-bold mb-2 text-white" data-id="element-105">{tomo.t}</h3>
                <p className="text-[#E65100] font-bold mb-4 text-sm uppercase tracking-wider" data-id="element-106">
                  Contenido Exclusivo
                </p>
                <p className="text-white/80 mb-6 text-sm leading-relaxed" data-id="element-107">
                  {tomo.d}
                </p>
                <ul className="space-y-3" data-id="element-108">
                  {tomo.f.map((feature, j) => <li key={j} className="flex items-center gap-3 text-sm text-white" data-id="element-109">
                      <CheckCircle2 size={16} className="text-white shrink-0" data-id="element-110" />{' '}
                      {feature}
                    </li>)}
                </ul>
              </div>)}
          </div>
        </div>
      </section>

      {/* Bonuses */}
      <section className="py-10 md:py-20 bg-[#ECEFF1]" data-id="element-111">
        <div className="container mx-auto px-4" data-id="element-112">
          <SectionTitle subtitle="Valor total bonos: $18.000 → GRATIS con tu pack" data-id="element-113">
            <span className="text-[#1A237E]" data-id="element-114">🎁 3 BONOS ESTRATÉGICOS</span>
          </SectionTitle>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-115">
            {[{
            n: '01',
            t: 'KIT EXPRESS',
            d: '15 actividades breves y listas para usar para abrir conversaciones sobre amenazas y bullying.',
            v: '$6.000'
          }, {
            n: '02',
            t: 'GUÍA DE FRASES',
            d: 'Las palabras exactas para hablar con estudiantes y familias sobre situaciones difíciles.',
            v: '$6.000'
          }, {
            n: '03',
            t: 'CUADERNILLO',
            d: 'Un workbook ilustrado para que alumnos y familias transformen la información en acción.',
            v: '$6.000'
          }].map((bono, i) => <div key={i} className="bg-white p-8 rounded-2xl shadow-sm border-t-8 border-[#E65100] relative overflow-hidden" data-id="element-116">
                <div className="absolute top-0 right-0 bg-[#2E7D32] text-white text-[10px] font-bold px-4 py-1 rounded-bl-lg uppercase" data-id="element-117">
                  Gratis
                </div>
                <span className="text-[#1A237E] font-bold text-sm mb-2 block" data-id="element-118">
                  BONO {bono.n}
                </span>
                <h4 className="text-xl font-bold mb-3 text-[#1A237E]" data-id="element-119">
                  {bono.t}
                </h4>
                <p className="text-[#37474F] text-sm mb-6 leading-relaxed" data-id="element-120">
                  {bono.d}
                </p>
                <p className="text-sm line-through text-[#9E9E9E]" data-id="element-121">{bono.v}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-4 md:py-20 bg-[#1A237E] text-white" id="pricing" data-id="element-122">
        <div className="container mx-auto px-4" data-id="element-123">
          <SectionTitle light subtitle="Acceso inmediato a todo el material en formato PDF." data-id="element-124">
            LLEVATE TU PACK
          </SectionTitle>

          <div className="flex justify-center mb-4 md:mb-12" data-id="element-125">
            <div className="bg-[#C62828] p-4 rounded-xl shadow-2xl scale-90 md:scale-100" data-id="element-126">
              <CountdownTimer data-id="element-127" />
            </div>
          </div>

          <div className="max-w-2xl mx-auto" data-id="element-128">
            <div className="bg-[#0D1B2A] rounded-3xl p-4 md:p-12 text-white shadow-2xl flex flex-col border-4 border-[#E65100] relative" data-id="element-129">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#E65100] text-white font-bold px-6 py-1 rounded-full text-[10px] md:text-xs uppercase tracking-widest" data-id="element-130">
                PACK COMPLETO ⭐
              </div>
              <div className="mb-4 md:mb-8" data-id="element-131">
                <h3 className="text-xl md:text-2xl font-bold text-white" data-id="element-132">
                  🚨 CÓDIGO EMERGENCIA ESCOLAR™
                </h3>
                <p className="text-white/60 text-xs md:text-sm" data-id="element-133">
                  4 Módulos + 3 Bonos
                </p>
              </div>
              <div className="mb-4 md:mb-8" data-id="element-134">
                <p className="text-[#9E9E9E] line-through text-base md:text-lg" data-id="element-135">
                  $48.000
                </p>
                <p className="text-4xl md:text-6xl font-bold text-[#E65100]" data-id="element-136">
                  $19.990{' '}
                  <span className="text-lg md:text-xl font-medium" data-id="element-137">ARS</span>
                </p>
              </div>
              <ul className="space-y-2 md:space-y-4 mb-6 md:mb-10 flex-grow" data-id="element-138">
                {['4 Módulos completos', 'Kit Express de Actividades', 'Guía de Frases y Guiones', 'Cuadernillo de Reflexión', '270+ páginas listas para usar', 'Acceso inmediato PDF'].map((item, i) => <li key={i} className="flex items-start gap-2 md:gap-3 text-white text-[11px] md:text-sm font-bold" data-id="element-139">
                    <CheckCircle2 size={14} className="text-[#2E7D32] shrink-0 md:w-[18px] md:h-[18px]" data-id="element-140" />
                    <span data-id="element-141">{item}</span>
                  </li>)}
              </ul>
              <CTAButton href="https://checkout.guiadigital360.online/cart/44890617806917:1" className="w-full bg-[#E65100] text-white text-xl md:text-2xl py-4 md:py-6" data-id="element-142">
                → QUIERO MI PACK AHORA
              </CTAButton>
              <div className="mt-4 md:mt-8 bg-[#C62828] text-white p-2 md:p-3 rounded-lg text-center font-bold text-[10px] md:text-base" data-id="element-143">
                ⚠️ Después sube $8.000 ARS. No vuelve.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-4 md:py-20 bg-white" data-id="element-144">
        <div className="container mx-auto px-4" data-id="element-145">
          <SectionTitle subtitle="La próxima situación difícil puede ser diferente." data-id="element-146">
            <span className="text-[#1A237E]" data-id="element-147">
              ⚖️ ¿CUÁNTO CUESTA NO TENERLO?
            </span>
          </SectionTitle>

          {/* Mobile View */}
          <div className="block md:hidden space-y-2 max-w-sm mx-auto" data-id="element-148">
            {[{
            s: 'Amenaza en WhatsApp',
            no: 'Parálisis',
            yes: 'Protocolo listo'
          }, {
            s: 'Reunión de padres',
            no: 'Sin palabras',
            yes: 'Agenda y guion'
          }, {
            s: 'Alumno cuenta algo',
            no: 'No saber qué decir',
            yes: 'Frases exactas'
          }, {
            s: 'Taller bullying',
            no: 'Improvisar',
            yes: '9 dinámicas'
          }, {
            s: '¿Qué es delito?',
            no: 'Miedo legal',
            yes: 'Marco explicado'
          }].map((row, i) => <div key={i} className="border border-slate-100 rounded-lg overflow-hidden shadow-sm" data-id="element-149">
                <div className="bg-[#F5F5F5] px-3 py-1 text-[10px] font-bold text-[#1A237E] uppercase" data-id="element-150">
                  {row.s}
                </div>
                <div className="grid grid-cols-2 text-[10px]" data-id="element-151">
                  <div className="bg-[#FFEBEE] p-2 text-[#C62828] flex items-center gap-1 italic border-r border-white" data-id="element-152">
                    <XCircle size={12} className="shrink-0" data-id="element-153" /> {row.no}
                  </div>
                  <div className="bg-[#E8F5E9] p-2 text-[#2E7D32] font-bold flex items-center gap-1" data-id="element-154">
                    <CheckCircle2 size={12} className="shrink-0" data-id="element-155" /> {row.yes}
                  </div>
                </div>
              </div>)}
            <div className="bg-[#1A237E] text-white p-3 text-center rounded-lg mt-4" data-id="element-156">
              <p className="text-xs font-bold" data-id="element-157">
                "Tu escuela preparada desde hoy." 🚨
              </p>
            </div>
          </div>

          {/* Desktop View */}
          <div className="hidden md:block max-w-4xl mx-auto overflow-hidden rounded-2xl border border-slate-200 shadow-xl" data-id="element-158">
            <table className="w-full text-left border-collapse" data-id="element-159">
              <thead data-id="element-160">
                <tr className="bg-[#1A237E] text-white" data-id="element-161">
                  <th className="p-4 md:p-6 font-bold" data-id="element-162">SITUACIÓN</th>
                  <th className="p-4 md:p-6 font-bold" data-id="element-163">❌ SIN EL PACK</th>
                  <th className="p-4 md:p-6 font-bold" data-id="element-164">✅ CON EL PACK</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-165">
                {[{
                s: 'Amenaza en WhatsApp',
                no: 'Parálisis e improvisación',
                yes: 'Módulo 2: protocolo listo en 60 min'
              }, {
                s: 'Reunión de padres urgente',
                no: 'Sin materiales ni palabras',
                yes: 'Módulo 4: agenda y guion completo'
              }, {
                s: 'Alumno que cuenta algo grave',
                no: 'No saber qué decir ni hacer',
                yes: 'Bono 2: frases exactas para ese momento'
              }, {
                s: 'Taller con alumnos sobre bullying',
                no: 'Improvisar sin recursos',
                yes: 'Módulo 3: 9 dinámicas listas'
              }, {
                s: '¿Qué es delito y qué no?',
                no: 'Miedo a decir algo incorrecto',
                yes: 'Módulo 1: marco legal explicado'
              }].map((row, i) => <tr key={i} className={`${i % 2 === 0 ? 'bg-white' : 'bg-[#FAFAFA]'} hover:bg-slate-50 transition-colors`} data-id="element-166">
                    <td className="p-4 md:p-6 text-sm font-bold text-[#1A237E] bg-[#F5F5F5]" data-id="element-167">
                      {row.s}
                    </td>
                    <td className="p-4 md:p-6 text-sm text-[#C62828] bg-[#FFEBEE] italic flex items-center gap-2" data-id="element-168">
                      <XCircle size={16} className="shrink-0" data-id="element-169" /> {row.no}
                    </td>
                    <td className="p-4 md:p-6 text-sm font-bold text-[#2E7D32] bg-[#E8F5E9] flex items-center gap-2" data-id="element-170">
                      <CheckCircle2 size={16} className="shrink-0" data-id="element-171" /> {row.yes}
                    </td>
                  </tr>)}
              </tbody>
            </table>
            <div className="bg-[#1A237E] text-white p-8 text-center border-t-4 border-[#E65100]" data-id="element-172">
              <p className="text-xl font-bold" data-id="element-173">
                "Tu escuela puede estar preparada desde hoy." 🚨
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials 2 */}
      <section className="py-10 md:py-20 bg-[#ECEFF1]" data-id="element-174">
        <div className="container mx-auto px-4" data-id="element-175">
          <SectionTitle subtitle="Más profesionales que ya lo usan" data-id="element-176">
            <span className="text-[#1A237E]" data-id="element-177">❤️ TESTIMONIOS</span>
          </SectionTitle>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-178">
            {[{
            n: 'Daniela M.',
            l: 'Mendoza · Profesora de ESI',
            q: 'El Módulo 3 lo usé la semana siguiente de comprarlo. Los alumnos estuvieron enganchados toda la hora.',
            i: 'DM'
          }, {
            n: 'Roberto P.',
            l: 'Buenos Aires · Director de primaria',
            q: 'El Módulo 1 me dio la seguridad legal que necesitaba para hablar con los padres sin tener miedo de decir algo mal.',
            i: 'RP'
          }, {
            n: 'Analía S.',
            l: 'Santa Fe · Orientadora escolar',
            q: 'El Bono 2 lo imprimí y lo tengo en el escritorio. Las frases exactas para cuando un alumno te cuenta algo difícil.',
            i: 'AS'
          }, {
            n: 'Claudia M.',
            l: 'La Plata · Preceptora de secundaria',
            q: 'El Módulo 4 lo usé para la reunión de padres del trimestre. Se fueron con materiales y compromisos.',
            i: 'CM'
          }].map((test, i) => <div key={i} className="bg-white p-8 rounded-2xl shadow-sm border-l-8 border-[#1A237E] flex gap-6 items-start" data-id="element-179">
                <div className="w-12 h-12 bg-[#E65100] rounded-full shrink-0 flex items-center justify-center font-bold text-white text-lg" data-id="element-180">
                  {test.i}
                </div>
                <div data-id="element-181">
                  <div className="flex gap-1 mb-2 text-yellow-500" data-id="element-182">
                    {[...Array(5)].map((_, j) => <Star key={j} size={14} fill="currentColor" data-id="element-183" />)}
                  </div>
                  <p className="text-[#37474F] italic mb-4" data-id="element-184">"{test.q}"</p>
                  <p className="font-bold text-[#1A237E] text-sm" data-id="element-185">{test.n}</p>
                  <p className="text-xs text-[#37474F]" data-id="element-186">{test.l}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Guarantee */}
      <section className="py-10 md:py-20 bg-[#E8F5E9]" data-id="element-187">
        <div className="container mx-auto px-4 text-center" data-id="element-188">
          <div className="max-w-3xl mx-auto bg-white p-8 md:p-12 rounded-3xl border-2 border-[#2E7D32] shadow-sm" data-id="element-189">
            <ShieldCheck size={64} className="text-[#2E7D32] mx-auto mb-6" data-id="element-190" />
            <h2 className="text-3xl font-bold mb-4 text-[#2E7D32]" data-id="element-191">
              GARANTÍA 7 DÍAS SIN PREGUNTAS
            </h2>
            <p className="text-lg text-[#37474F] mb-8 leading-relaxed" data-id="element-192">
              Si abrís el pack y no es lo que esperabas: <br data-id="element-193" />
              <span className="text-3xl font-bold text-[#2E7D32] block mt-4" data-id="element-194">
                DEVOLUCIÓN DEL 100%
              </span>
              <span className="text-sm text-[#37474F] mt-2 block" data-id="element-195">
                sin formularios ni vueltas.
              </span>
            </p>
            <p className="text-xs text-[#37474F] uppercase tracking-widest font-bold" data-id="element-196">
              Tasa de devoluciones: menos del 1%
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-10 md:py-20 bg-white" data-id="element-197">
        <div className="container mx-auto px-4" data-id="element-198">
          <SectionTitle subtitle="Despejá tus dudas y empezá hoy mismo." data-id="element-199">
            <span className="text-[#1A237E]" data-id="element-200">❓ LO QUE MÁS NOS PREGUNTAN</span>
          </SectionTitle>
          <div className="max-w-3xl mx-auto space-y-4" data-id="element-201">
            {[{
            q: '¿Para quién es este pack?',
            a: 'Para docentes, preceptores, orientadores, directivos, profesionales que dan charlas y familias que quieren herramientas concretas sobre seguridad digital escolar en Argentina.'
          }, {
            q: '¿Cómo recibo el material?',
            a: 'Inmediatamente después del pago recibís un email con el link de descarga. En menos de 5 minutos tenés todo en tu dispositivo.'
          }, {
            q: '¿Qué lo diferencia de otros materiales?',
            a: 'Está 100% adaptado al contexto legal argentino, con leyes vigentes, lenguaje docente y situaciones reales de escuelas argentinas. No es teoría: son herramientas listas para usar.'
          }, {
            q: '¿Tiene garantía real?',
            a: 'Sí. 7 días sin preguntas. Si abrís el pack y no es lo que esperabas te devolvemos el 100% sin formularios ni vueltas.'
          }].map((faq, i) => <details key={i} className="group bg-[#E8EAF6] rounded-xl overflow-hidden" data-id="element-202">
                <summary className="flex items-center justify-between p-6 cursor-pointer font-bold text-[#1A237E] list-none" data-id="element-203">
                  {faq.q}
                  <ChevronDown size={20} className="text-[#E65100] group-open:rotate-180 transition-transform" data-id="element-204" />
                </summary>
                <div className="px-6 pb-6 bg-white text-[#37474F] leading-relaxed text-sm border-l-8 border-[#E65100]" data-id="element-205">
                  {faq.a}
                </div>
              </details>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-10 md:py-20 bg-[#1A237E] text-white text-center" data-id="element-206">
        <div className="container mx-auto px-4" data-id="element-207">
          <SectionTitle light subtitle="Tu escuela puede estar preparada desde hoy. 🚨" data-id="element-208">
            ⏰ EL PRECIO CIERRA EN:
          </SectionTitle>
          <div className="flex justify-center mb-12" data-id="element-209">
            <div className="bg-[#C62828] p-4 rounded-xl" data-id="element-210">
              <CountdownTimer data-id="element-211" />
            </div>
          </div>

          <div className="max-w-2xl mx-auto mb-12" data-id="element-212">
            <div className="bg-[#0D1B2A] rounded-3xl p-8 md:p-12 text-white shadow-2xl flex flex-col border-4 border-[#E65100] relative" data-id="element-213">
              <h3 className="text-2xl font-bold mb-4" data-id="element-214">
                🚨 CÓDIGO EMERGENCIA ESCOLAR™
              </h3>
              <p className="text-[#9E9E9E] line-through text-lg" data-id="element-215">$48.000</p>
              <p className="text-6xl font-bold text-[#E65100] mb-8" data-id="element-216">
                $19.990 ARS
              </p>
              <CTAButton href="https://checkout.guiadigital360.online/cart/44890617806917:1" className="w-full bg-[#E65100] text-white text-2xl py-6" data-id="element-217">
                → QUIERO EL PACK COMPLETO AHORA
              </CTAButton>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-4 md:gap-8" data-id="element-218">
            <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-[#1A237E] font-bold text-xs uppercase tracking-widest" data-id="element-219">
              <ShieldCheck size={16} className="text-[#2E7D32]" data-id="element-220" /> Pago 100%
              Seguro
            </div>
            <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-[#1A237E] font-bold text-xs uppercase tracking-widest" data-id="element-221">
              <CheckCircle2 size={16} className="text-[#2E7D32]" data-id="element-222" /> Garantía de
              Satisfacción
            </div>
            <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-[#1A237E] font-bold text-xs uppercase tracking-widest" data-id="element-223">
              <Zap size={16} className="text-[#2E7D32]" data-id="element-224" /> Acceso Instantáneo
            </div>
          </div>

          <div className="mt-12 bg-[#C62828] text-white p-3 rounded-lg inline-block font-bold" data-id="element-225">
            ⚠️ Después sube $8.000 ARS. No vuelve.
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 bg-[#0D1B2A] text-white/60 border-t border-white/5" data-id="element-226">
        <div className="container mx-auto px-4 text-center" data-id="element-227">
          <p className="font-bold text-white text-2xl mb-4 font-display" data-id="element-228">
            🚨 CÓDIGO EMERGENCIA ESCOLAR™
          </p>
          <p className="text-sm mb-8 max-w-2xl mx-auto leading-relaxed text-white/70" data-id="element-229">
            El pack de materiales pedagógicos N°1 para docentes, directivos y
            familias que necesitan herramientas concretas ante amenazas,
            bullying, grooming y violencia digital en Argentina.
          </p>
          <div className="flex flex-col md:flex-row justify-center gap-6 md:gap-12 mb-12 text-sm" data-id="element-230">
            <a href="mailto:contacto@codigoemergenciaescolar.com.ar" className="text-[#E65100] hover:text-white transition-colors flex items-center justify-center gap-2" data-id="element-231">
              <MessageSquare size={16} data-id="element-232" />{' '}
              contacto@codigoemergenciaescolar.com.ar
            </a>
            <div className="flex items-center justify-center gap-2 text-[#E65100]" data-id="element-233">
              <Star size={16} data-id="element-234" /> @codigoemergenciaescolar
            </div>
          </div>
          <div className="w-full h-px bg-white/5 mb-8" data-id="element-235"></div>
          <p className="text-xs text-[#B0BEC5]" data-id="element-236">
            © Código Emergencia Escolar™ 2026 — Todos los derechos reservados.
          </p>
          <p className="text-white mt-4 flex items-center justify-center gap-2 font-bold" data-id="element-237">
            "Tu escuela puede estar preparada desde hoy."{' '}
            <span className="text-[#E65100]" data-id="element-238">🚨</span>
          </p>
        </div>
      </footer>
    </div>;
}